import {
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  Box,
  IconButton,
  Typography,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DashboardIcon from "@mui/icons-material/DashboardOutlined";
import BusIcon from "@mui/icons-material/DirectionsBusOutlined";
import ListIcon from "@mui/icons-material/BadgeOutlined";
import InsertDriveFileIcon from "@mui/icons-material/AssessmentOutlined";
import SettingsIcon from "@mui/icons-material/SettingsOutlined";
import AddUsers from "@mui/icons-material/PersonAddAlt1Outlined";
import LogoutIcon from "@mui/icons-material/LogoutOutlined";
import AddIcon from "@mui/icons-material/Add";
import { useLocation, useNavigate } from "react-router-dom";
import logo from "../../assets/images/login.png";
import { useDispatch, useSelector } from "react-redux";
import { logout } from '../../redux/slices/authSlice';

const drawerWidth = 232;

// Sidebar palette, shared with the mobile top bar in Layout.
export const SIDEBAR_COLORS = {
  background: "#123047",
  text: "#EAF2FF",
  muted: "#B8C7D9",
  icon: "#DCE7F5",
  iconActive: "#FFFFFF",
  activeBg: "rgba(37, 99, 235, 0.22)",
  accent: "#60A5FA",
  primary: "#2563EB",
  hover: "rgba(255, 255, 255, 0.07)",
  divider: "rgba(255, 255, 255, 0.10)",
  footer: "#C7D2E0",
};
export const SIDEBAR_BACKGROUND = SIDEBAR_COLORS.background;

// Each item stays highlighted on its child pages (details, add, edit, view).
const isActivePath = (pathname, prefixes) =>
  prefixes.some((p) => pathname === p || pathname.startsWith(`${p}/`));

const menuItemSx = {
  minHeight: 46,
  px: 1.5,
  mb: 0.5,
  borderRadius: "10px",
  position: "relative",
  color: SIDEBAR_COLORS.text,
  transition: "background-color 160ms ease, color 160ms ease",
  "& .MuiListItemIcon-root": {
    minWidth: 0,
    mr: 1.5,
    color: SIDEBAR_COLORS.icon,
    fontSize: 20,
    transition: "color 160ms ease",
  },
  "& .MuiSvgIcon-root": { fontSize: 20 },
  "&:hover": { backgroundColor: SIDEBAR_COLORS.hover },
  "&.Mui-focusVisible": {
    backgroundColor: SIDEBAR_COLORS.hover,
    outline: `2px solid ${SIDEBAR_COLORS.accent}`,
    outlineOffset: -2,
  },
  "&.Mui-selected, &.Mui-selected:hover": {
    backgroundColor: SIDEBAR_COLORS.activeBg,
    color: SIDEBAR_COLORS.iconActive,
  },
  "&.Mui-selected .MuiListItemIcon-root": { color: SIDEBAR_COLORS.iconActive },
  "&.Mui-selected::before": {
    content: '""',
    position: "absolute",
    left: 0,
    top: 10,
    bottom: 10,
    width: 3,
    borderRadius: "0 3px 3px 0",
    backgroundColor: SIDEBAR_COLORS.accent,
  },
};

const MenuItem = ({ icon, label, selected, onClick }) => (
  <ListItemButton
    selected={selected}
    onClick={onClick}
    aria-current={selected ? "page" : undefined}
    sx={menuItemSx}
  >
    <ListItemIcon>{icon}</ListItemIcon>
    <Typography
      fontSize={14.5}
      fontWeight={selected ? 600 : 500}
      fontFamily={"MyFontNormal"}
      sx={{ textTransform: "capitalize", lineHeight: 1.3 }}
    >
      {label}
    </Typography>
  </ListItemButton>
);

const SideMenuBar = ({ sidebar, setSidebar, isMobile, drawerId }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const roleId = sessionStorage.getItem("role_id");
  const { role_id } = useSelector((state) => state.auth);
  const effectiveRole = role_id || roleId;

  const handleNavigation = (path) => {
    navigate(path)
    setSidebar(false)
  }
  const handleLogout = () => {
    if (window.confirm("Are you sure you want to logout?")) {
      dispatch(logout());
      navigate("/login", { replace: true });
    }
  };

  const drawerContent = (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        backgroundColor: SIDEBAR_COLORS.background,
        color: SIDEBAR_COLORS.text,
      }}
    >
      {/* Logo + Close (only on mobile) */}
      <Box
        sx={{
          position: "relative",
          px: 2,
          pt: 2.5,
          pb: 2,
          borderBottom: `1px solid ${SIDEBAR_COLORS.divider}`,
        }}
      >
        <Box
          component="img"
          src={logo}
          alt="Police Verification"
          sx={{
            display: "block",
            mx: "auto",
            width: 104,
            height: "auto",
            filter: "drop-shadow(0 4px 10px rgba(0, 0, 0, 0.25))",
          }}
        />

        {isMobile && (
          <IconButton
            onClick={() => setSidebar(false)}
            aria-label="Close navigation menu"
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              width: 40,
              height: 40,
              color: SIDEBAR_COLORS.icon,
              "&:hover": { backgroundColor: SIDEBAR_COLORS.hover },
            }}
          >
            <CloseIcon />
          </IconButton>
        )}

        {(effectiveRole === "Nw==" || effectiveRole === "Mg==") ? (
          <Button
            variant="contained"
            disableElevation
            fullWidth
            startIcon={<AddIcon />}
            sx={{
              mt: 2,
              height: 44,
              borderRadius: "10px",
              backgroundColor: SIDEBAR_COLORS.primary,
              color: "#fff",
              "&:hover": { backgroundColor: "#1D4ED8" },
            }}
            onClick={() => {
              navigate('/add-driver');
              setSidebar(false)
            }}
          >
            <Typography
              fontSize={14}
              fontWeight={600}
              sx={{ textTransform: "none" }}
              fontFamily={"MyFontNormal"}
            >
              Add Driver/Conductor
            </Typography>
          </Button>
        )
          : null}
      </Box>

      {/* Menu items */}
      <Box sx={{ flexGrow: 1, overflowY: "auto", px: 1.5, pt: 2, pb: 1 }}>
        <Typography
          component="div"
          sx={{
            px: 1.5,
            mb: 1,
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: SIDEBAR_COLORS.muted,
          }}
        >
          Menu
        </Typography>

        <List component="nav" aria-label="Main navigation" disablePadding>
          <MenuItem
            icon={<DashboardIcon />}
            label="Dashboard"
            selected={isActivePath(pathname, ["/home"])}
            onClick={() => handleNavigation("/home")}
          />

          <MenuItem
            icon={<ListIcon />}
            label="Driver Conductor List"
            selected={isActivePath(pathname, ["/drivers", "/add-driver", "/conductors", "/add-conductor"])}
            onClick={() => handleNavigation("/drivers")}
          />

          {effectiveRole === "Nw==" && (
            <MenuItem
              icon={<BusIcon />}
              label="Bus Management"
              selected={isActivePath(pathname, ["/buses", "/bus", "/vehicles"])}
              onClick={() => handleNavigation("/buses")}
            />
          )}

          {effectiveRole !== "Nw==" && (
            <MenuItem
              icon={<InsertDriveFileIcon />}
              label="MIS Report"
              selected={isActivePath(pathname, ["/schools", "/school", "/policestation"])}
              onClick={() => handleNavigation("/schools")}
            />
          )}

          {effectiveRole === "OQ==" && (
            <MenuItem
              icon={<AddUsers />}
              label="Add Users"
              selected={isActivePath(pathname, ["/users", "/AddUsers"])}
              onClick={() => handleNavigation("/users")}
            />
          )}

          <MenuItem
            icon={<SettingsIcon />}
            label="Setting"
            selected={isActivePath(pathname, ["/profile", "/changePassword"])}
            onClick={() => handleNavigation("/profile")}
          />
        </List>
      </Box>

      {/* Logout + footer */}
      <Box sx={{ px: 1.5, pt: 1, borderTop: `1px solid ${SIDEBAR_COLORS.divider}` }}>
        <MenuItem icon={<LogoutIcon />} label="Logout" selected={false} onClick={handleLogout} />
      </Box>
      <Box sx={{ px: 2, pt: 0.5, pb: 2, textAlign: "center" }}>
        <Typography sx={{ fontSize: 11.5, color: SIDEBAR_COLORS.footer, opacity: 0.8 }}>
          © 2025 Police Verification
        </Typography>
      </Box>
    </Box>
  );

  const paperSx = {
    width: drawerWidth,
    boxSizing: "border-box",
    backgroundColor: SIDEBAR_COLORS.background,
    borderRight: "none",
  };

  return (
    <>
      {isMobile ? (
        // ✅ Mobile: overlay
        // Temporary drawer closes on backdrop click and Escape, traps focus while open
        // and returns it to the menu button afterwards.
        <Drawer
          id={drawerId}
          anchor="left"
          open={sidebar}
          onClose={() => setSidebar(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            "& .MuiDrawer-paper": { ...paperSx, width: 260, maxWidth: "85vw" },
          }}
        >
          {drawerContent}
        </Drawer>
      ) : (
        // ✅ Desktop: permanent
        <Drawer
          variant="permanent"
          open
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            "& .MuiDrawer-paper": paperSx,
          }}
        >
          {drawerContent}
        </Drawer>
      )}
    </>
  );
};

export default SideMenuBar;
